import { startOfMonth, startOfQuarter, startOfYear, parseISO, formatISO, format, } from 'date-fns';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { InteractionManager, View } from 'react-native';
import { useAnimatedScrollHandler, useSharedValue, } from 'react-native-reanimated';
import { TextChip, IconChip, ControlsRow } from 'design-system/controls';
import useAnalyticsFilter from 'design-system/analyticsFilter';
import MultiSelectAccountModal from 'design-system/MultiSelectAccountModal';
import IconFilterBold from 'design-system/icons/IconFilterBold';
import useColors from 'hooks/useColors';
import Config from 'utils/packages/configs';
import useAppRating from 'hooks/useAppRating';
import { selectListAccounts } from 'reducers/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { filterChanged, resetAnalyticsFilter } from 'actions';
import { formatMonth, formatPeriod } from 'utils/formatting';
import { isTimeDifference15mins } from '../utils/utils';
import AnalyticsChartSwitcher from './AnalyticsChartSwitcher';
const mapFilterTypeToStep = {
    week: 'isoWeek',
    month: 'month',
    quarter: 'quarter',
    year: 'year',
    custom: 'custom',
};
export const getFromFormatted = (args) => {
    switch (args.type) {
        case 'week':
        case 'custom':
            return args.from;
        case 'month':
            return startOfMonth(new Date(args.year, args.month - 1));
        case 'quarter':
            return startOfQuarter(new Date(args.year, (args.quarter - 1) * 3, 1));
        case 'year':
            return startOfYear(new Date(args.year, 0, 1));
        default:
            return new Date();
    }
};
const formatForDebug = (period) => `${format(period.from, 'MMMM d, HH:mm')} to ${format(new Date(period.to), 'HH:mm')}`;
export function getDatesChipTitle(month, payPeriod, step) {
    if (Config?.IS_PROD === 'false' && month && !step) {
        const isUsing15MinPeriod = isTimeDifference15mins(month.to, month.from);
        if (isUsing15MinPeriod) {
            return formatForDebug(month); // debug
        }
    }
    if (month) {
        return formatPeriod(month);
    }
    // month may have not yet loaded
    if (payPeriod) {
        const isUsing15MinPeriod = isTimeDifference15mins(payPeriod.to, payPeriod.from);
        if (isUsing15MinPeriod) {
            return formatForDebug(payPeriod); // debug
        }
        return formatPeriod(payPeriod);
    }
    return formatMonth(startOfMonth(new Date()));
}
const AnalyticsControls = ({ month, payPeriod, step: currentStep, onLayoutChartSettings, accountIds: currentAccountIds, }) => {
    const title = useMemo(() => getDatesChipTitle(month, payPeriod, currentStep), [currentStep, month, payPeriod]);
    const colors = useColors();
    const dispatch = useAppDispatch();
    const [show, hide] = useAnalyticsFilter();
    const rateApp = useAppRating();
    const onSave = useCallback((response) => {
        hide();
        rateApp();
        InteractionManager.runAfterInteractions(() => {
            const step = mapFilterTypeToStep[response.type];
            dispatch(filterChanged(step, formatISO(getFromFormatted(response)), (response.type === 'custom'
                ? response.to
                : new Date()).toISOString(), currentAccountIds));
        });
    }, [currentAccountIds, hide, rateApp]);
    const onReset = useCallback(() => {
        hide();
        InteractionManager.runAfterInteractions(() => {
            dispatch(resetAnalyticsFilter());
        });
    }, [hide]);
    const onPressFilter = () => {
        const from = parseISO(month?.from ?? '');
        show(onSave, currentStep !== undefined &&
            ((currentStep === 'month' && payPeriod !== undefined) ||
                currentStep !== 'month')
            ? onReset
            : undefined, month
            ? {
                weeklyRange: currentStep === 'isoWeek'
                    ? [from, parseISO(month.to)]
                    : undefined,
                monthly: currentStep === 'month' || (!currentStep && !payPeriod)
                    ? {
                        currentMonth: from.getMonth(),
                        currentYear: from.getFullYear(),
                    }
                    : undefined,
                quarterly: currentStep === 'quarter'
                    ? {
                        currentQuarter: Math.floor(from.getMonth() / 3) + 1,
                        currentYear: from.getFullYear(),
                    }
                    : undefined,
                yearly: currentStep === 'year' ? from.getFullYear() : undefined,
                custom: currentStep === 'custom' ? [from, parseISO(month.to)] : undefined,
            }
            : undefined);
    };
    const accounts = useAppSelector(selectListAccounts);
    const accountsWithoutHidden = useMemo(() => {
        const filtered = accounts.filter((acc) => !acc.isHidden);
        return filtered;
    }, [accounts]);
    const alreadySelectedAccounts = useMemo(() => {
        /* No accounts means they have everything selected */
        if (currentAccountIds) {
            return currentAccountIds;
        }
        return accountsWithoutHidden.map((acc) => acc.id);
    }, [currentAccountIds, accountsWithoutHidden]);
    const [selectedAllAccounts, setSelectedAllAccounts] = useState(alreadySelectedAccounts.length === accountsWithoutHidden.length);
    const onSelectedAccounts = useCallback(async (selectedAccounts) => {
        const selectedAll = accountsWithoutHidden.length === selectedAccounts.length;
        setSelectedAllAccounts(selectedAll);
        const hasFilter = !selectedAll || currentStep;
        try {
            if (hasFilter) {
                dispatch(filterChanged(currentStep, formatISO(month?.from ?? new Date()), currentStep === 'custom' && month?.to
                    ? formatISO(month.to)
                    : formatISO(new Date()), selectedAll ? undefined : selectedAccounts));
            }
            else {
                dispatch(resetAnalyticsFilter());
            }
        }
        catch {
            // noop
        }
    }, [accountsWithoutHidden.length, currentStep, month?.from, month?.to]);
    const scrollValue = useSharedValue(0);
    const onScroll = useAnimatedScrollHandler((handle) => {
        scrollValue.value = handle.contentOffset.y;
    }, []);
    const onPressAccountsFilter = useCallback(() => {
        if (accounts.length) {
            MultiSelectAccountModal.show({
                onScroll,
                scrollValue,
                accounts,
                alreadySelected: alreadySelectedAccounts,
                onSelected: onSelectedAccounts,
                showSelectAll: true,
            });
        }
    }, [
        accounts,
        alreadySelectedAccounts,
        onScroll,
        onSelectedAccounts,
        scrollValue,
    ]);
    return (<ControlsRow>
      <TextChip text={title} onPress={onPressFilter}/>
      <IconChip onPress={onPressAccountsFilter}>
        <IconFilterBold color={colors.elements.secondary} dot={!selectedAllAccounts}/>
      </IconChip>
      <View onLayout={onLayoutChartSettings}>
        <AnalyticsChartSwitcher />
      </View>
    </ControlsRow>);
};
export default memo(AnalyticsControls);
